import React from "react";
import {Box, Checkbox, Chip, Grid, Input, Stack, Tooltip} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Typography from "@mui/material/Typography";
import {StyledDivider, StyledSlider2} from "./StyledComponents";
import ReactLoading from "react-loading";
import {limitNumber, openEndPoint} from "../../../utils/utils";
import {isOwner} from "../utils/displaySettingsUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateChip from "./CreateButton";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import Button from '@mui/material/Button';
import {
	DELETED_SLIDE_ERROR,
	PERMISSION_DENIED_ERROR,
	SLIDE_NOT_FOUND_ERROR
} from "../../../redux_constants/maps.state.const";

export const getEyeCheckbox = (checked, defaultChecked, onChange, icon, checkedIcon, key) =>
	<Checkbox
		key={key}
		edge={"start"}
		size={"small"}
		checked={checked}
		defaultChecked={defaultChecked}
		icon={icon ? icon : <VisibilityIcon sx={{color: '#8a8a8a'}} fontSize={'small'}/>}
		checkedIcon={checkedIcon ? checkedIcon : <VisibilityIcon color={'secondary'} fontSize={'small'}/>}
		onClick={onChange}
	/>

export const HorizontalDivider = props => <StyledDivider orientation={"horizontal"} flexItem {...props}/>;

export const VerticalDivider = props => <StyledDivider orientation={"vertical"} flexItem {...props}/>;

export const getInfoRow = (label, value) =>
	<Stack direction={"row"} alignItems={"center"} height={30}>
		<Typography sx={{fontWeight: 'bold', width: '45%'}}>{label} </Typography>
		<VerticalDivider sx={{mx: 0.5}}/>
		<Typography>{value}</Typography>
	</Stack>

export const Loading = ({text, color, children, ...props}) =>
	<Stack direction={"column"} justifyContent={"center"} alignItems={"center"}>
		<ReactLoading type={"cylon"} color={"#fff"} height={50} width={50} {...props}/>
		{(text || children) && <Typography sx={{color: color || props.color || '#fff'}}>{text || children}</Typography>}
	</Stack>

export const LoadingText = (props, color = "") => <Loading text={"Loading..."} color={color} {...props}/>

export const ReturnToDashboard = ()  => {
	return (
			<Button variant="contained" startIcon={<ExitToAppIcon />}
					sx={{backgroundColor: '#330470'}}
					onClick={(event) =>
						openEndPoint("/dashboard/", false, true)}>
				Return To Dashboard
			</Button>
	)
}
export const SlideError = (props) => {
	let error_message;
	switch (props.type){
		case DELETED_SLIDE_ERROR:
			error_message = "The slide you are trying to access has been deleted or does not exists.";
			break;
		case SLIDE_NOT_FOUND_ERROR:
			error_message = "The slide you are trying to access does not exist.";
			break;
		case PERMISSION_DENIED_ERROR:
			error_message = "You do not have access to this slide. Please ask your administrator to share this slide with you.";
			break;
	}
	return (
		<Box
			sx={{ display: 'inline-flex', 'flex-direction': 'column', gap:'10px'  }}
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="80vh"
			minWidth="100%"
		>
			<Typography variant="h6">
				{error_message}
			</Typography>
			<ReturnToDashboard />
		</Box>
	)
}

export const ScreenWarningText = props => <Loading text={"Can't open Scan in this screen resolution..."}
												   color={'secondary'} {...props}/>

export const SliderRow = ({label, icon, defaultValue, value, min, max, step, update, disabled, hideInput, labelColor, color}) => {
	return <Stack direction={"column"} spacing={0.5}>
		{label && <Typography variant={"caption"} fontSize={15} color={labelColor ? labelColor : "whitesmoke"}>{label}</Typography>}
		<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} spacing={3}>
			{icon}
			<StyledSlider2 defaultValue={defaultValue} value={value} min={min} max={max} step={step || 1} color={color}
							disabled={disabled}
							onChange={(event, value) => update(limitNumber(value, min, max))}/>
			{!hideInput &&
				<Input defaultValue={defaultValue} value={value} size={'small'} sx={{width: '50px'}}
						onChange={event => !disabled && update(limitNumber(event.target.value, min, max))}
						inputProps={{min: min, max: max, type: 'number', 'aria-labelledby': 'input-slider', step:step||1,}}/>
			}
		</Stack>
	</Stack>;
}

export const SettingChips = ({settings, onClick, selectedId, onDelete, showDelete, onCreate, showCreate, canCreate}) =>
	<Grid width={"100%"}>
		{canCreate && showCreate ?
			<CreateChip
				onCreate={onCreate}
				label={"+ new"}
				sx={{ width: "30%", borderRadius: 1, margin: 0.25, borderStyle: "dashed" }}
			/>
			: <></>
		}
		{settings.map(setting => setting &&
			<Tooltip followCursor={true} title={setting.name} placement={"top-start"}>
				<Chip label={setting.name} key={setting.id} size={"small"}
				      onClick={() => onClick(setting)}
				      onDelete={isOwner(setting) && setting.id !== selectedId && showDelete && (() => onDelete(setting))}
				      deleteIcon={<DeleteIcon/>} variant={"filled"}
				      color={setting.id === selectedId ? "secondary" : "primary"}
				      sx={{
					      width: "30%", margin: 0.25, borderRadius: 1, justifyContent: "space-between",
					      opacity: !isOwner(setting) && setting.id !== selectedId  && !this.hasEditSlideSettingPermission() ? 0.6 : 1
				      }}
				/>
			</Tooltip>
		)}
	</Grid>

export const AppTitle = ({title, children}) =>
	<Typography align={"center"} variant={"overline"} fontSize={20} color={"whitesmoke"}>
		{title || children}
	</Typography>

export const AppSubtitle = ({title, children}) =>
	<Typography align={"center"} variant={"h6"} fontSize={18} color={"whitesmoke"}>
		{title || children}
	</Typography>

export const AppRow = ({title, value, children}) =>
	<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{paddingX: 1}}>
		<Typography color={"whitesmoke"}>{title} </Typography>
		<Typography color={"whitesmoke"} width={"50%"}>{value || children}</Typography>
	</Stack>
